import { FormControl, FormErrorMessage, FormLabel, type FormLabelProps, Tooltip } from '@chakra-ui/react'
import { Box } from '@chakra-ui/react'
import { appColors } from '@theme/foundations/colors'
import type { ReactElement, ReactNode } from 'react'
import ReactSelect, { type FormatOptionLabelMeta, type Props as SelectProps, type StylesConfig } from 'react-select'
import { BaseTooltip, type BaseTooltipProps } from '../Tooltip'
import { FlexRow } from '../flex'

interface Option {
  value: any
  label: string
  tag?: { label: string }
  isDisabled?: boolean
}
// TODO: Simpler version of select using the newest styles. Once we replace other selects, we can remove the old one
interface IFormSelectProps extends Omit<SelectProps, 'onChange'> {
  name: string
  label?: string
  isInvalid?: boolean
  tooltip?: React.ReactNode
  width?: string
  height?: string
  controlProps?: any
  errorMessage?: string
  noIndicatorAnimation?: boolean
  anchor?: HTMLElement | null
  isRequired?: boolean
  isMulti?: boolean
  labelProps?: FormLabelProps
  labelRightElement?: ReactNode
  tooltipProps?: BaseTooltipProps
  noOptionsMessage?: () => ReactElement
  onChange?: (value: Option['value'], actionMeta: any, option: Option) => void
  dataTestId?: string
  isDisabled?: boolean
  disabledTooltip?: React.ReactNode
  indicatorStyling?: React.CSSProperties
  // Rendered below the select element (e.g. an InfoBanner). Visible whether or not the menu is open.
  hint?: ReactNode
}

const customStyles = ({
  isInvalid,
  width,
  height,
  controlProps,
  noIndicatorAnimation,
  indicatorStyling,
  isMulti,
  minHeight,
  isPortaled,
}: {
  isInvalid: boolean
  width?: string
  height?: string
  controlProps?: any
  noIndicatorAnimation?: boolean
  indicatorStyling?: React.CSSProperties
  isMulti?: boolean
  minHeight?: string
  isPortaled?: boolean
}): StylesConfig => ({
  option: (_, state) => {
    const isDisabled = !!state.isDisabled
    const color = isDisabled ? appColors.mutedForeground : state.isSelected ? appColors.primaryForeground : appColors.foreground
    const backgroundColor = state.isSelected && !isDisabled ? appColors.accentPrimary : appColors.background
    return {
      display: 'block',
      color,
      fontSize: '14px',
      backgroundColor,
      minHeight: '32px',
      ':hover': isDisabled
        ? { cursor: 'not-allowed' }
        : {
            backgroundColor: state.isSelected ? appColors.accentPrimary : appColors.muted,
            cursor: 'pointer',
            borderRadius: '8px',
          },
      padding: '8px 12px',
      whiteSpace: 'normal',
      wordWrap: 'break-word',
      overflowWrap: 'anywhere',
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      borderRadius: '8px',
      margin: '2px',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
    }
  },

  control: (_, state) => {
    const boxShadow = isInvalid ? `0px 10px 12px -3px rgba(0, 0, 0, 0.02), 0px 4px 2px -2px rgba(0, 0, 0, 0.02)` : 'none'

    const borderColor = state.isFocused ? appColors.primary : isInvalid ? appColors.red[500] : appColors.border

    return {
      color: state.isDisabled ? '#A0A0A0' : '#0D0D0D',
      fontSize: '14px',
      fontWeight: '400px',
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: '8px',
      minHeight: minHeight ?? '32px',
      height: height ?? '32px',
      width: width ?? undefined,
      maxWidth: '100%',
      minWidth: 0,
      overflow: 'hidden',
      border: `1px solid ${appColors.border}`,
      display: 'flex',
      alignItems: 'center',
      boxShadow,
      ':hover': {
        borderColor: state.isDisabled ? '#DEDEDE' : state.isFocused ? undefined : '#B4B4B4',
        cursor: state.isDisabled ? 'not-allowed' : 'default',
      },
      '&:focus': {
        borderWidth: '2px',
      },
      ':active': {
        borderWidth: '2px',
      },
      backgroundColor: state.isDisabled ? '#F5F5F5' : '#FFF',
      borderColor: state.isDisabled ? '#E0E0E0' : borderColor,
      borderRadius: '8px',
      opacity: state.isDisabled ? 0.6 : 1,
      cursor: state.isDisabled ? 'not-allowed' : 'default',
      ...controlProps,
    }
  },

  singleValue: (provided, state) => ({
    ...provided,
    opacity: state.isDisabled ? 0.5 : 1,
    transition: 'opacity 300ms',
    color: '#0D0D0D',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  }),

  placeholder: (provided, state) => ({
    ...provided,
    color: state.isDisabled ? '#A0A0A0' : appColors.gray[400],
    fontSize: '14px',
    fontWeight: '86',
    lineHeight: '24px',
    letterSpacing: '0.21px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  }),

  valueContainer: () => ({
    width: width ? `calc(${width} - 44px)` : '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    marginLeft: '0',
    marginTop: 0,
    marginBottom: 0,
    marginRight: 'auto',
    padding: '0',
    justifyContent: 'space-between',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  }),

  dropdownIndicator: (provided, state) => {
    if (noIndicatorAnimation) return provided
    const transform = state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none'
    const transition = 'transform 0.2s ease-in-out'
    return {
      ...provided,
      ...indicatorStyling,
      transform,
      transition,
    }
  },

  multiValue: provided => ({
    ...provided,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal',
  }),

  multiValueLabel: provided => ({
    ...provided,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal',
    maxWidth: '100%',
  }),

  input: () => ({
    position: isMulti ? 'relative' : 'absolute',
  }),

  menu: provided => ({
    ...provided,
    top: isPortaled ? provided.top : '45px',
    width: width ?? provided.width ?? '100%',
    minWidth: 0,
    maxWidth: '100%',
    position: isPortaled ? provided.position : 'absolute',
    backgroundColor: '#FFF',
    border: `1px solid ${appColors.border}`,
    borderRadius: '8px',
    boxShadow: '0px 10px 12px -3px rgba(0, 0, 0, 0.02), 0px 4px 2px -2px rgba(0, 0, 0, 0.02)',
    zIndex: isPortaled ? 1500 : 25,
    height: 'fit-content',
    padding: '4px',
    overflow: 'hidden',
    overflowX: 'hidden',
  }),

  menuPortal: provided => ({
    ...provided,
    zIndex: 1500,
  }),

  menuList: provided => ({
    ...provided,
    overflowX: 'hidden',
  }),

  indicatorSeparator: () => ({
    display: 'none',
  }),

  groupHeading: provided => ({
    ...provided,
    color: appColors.mutedForeground,
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    padding: '8px 12px 4px',
    margin: 0,
  }),
})

const renderOptionWithTag = (data: unknown, meta: FormatOptionLabelMeta<unknown>): ReactNode => {
  const option = data as Option
  if (meta.context === 'value' || !option.tag) return option.label
  if (option.isDisabled) {
    return (
      <Tooltip label={option.tag.label} bg='white' color='gray.800' fontWeight='normal' p='3' borderRadius='base' hasArrow>
        <Box width='100%' whiteSpace='normal' overflowWrap='anywhere'>
          {option.label}
        </Box>
      </Tooltip>
    )
  }

  return (
    <FlexRow alignItems='center' gap='8px' width='100%'>
      <Box flex='1' minWidth={0} whiteSpace='normal' overflowWrap='anywhere'>
        {option.label}
      </Box>
      <Box
        flexShrink={0}
        height='20px'
        paddingX='8px'
        paddingY='4px'
        borderRadius='2px'
        borderWidth='1px'
        borderStyle='solid'
        borderColor={appColors.blue['100']}
        backgroundColor={appColors.blue['50']}
        color={appColors.accentPrimary}
        fontSize='12px'
        lineHeight='16px'
        display='flex'
        alignItems='center'
      >
        {option.tag.label}
      </Box>
    </FlexRow>
  )
}

export const FormSelect = ({
  name,
  isInvalid = false,
  onChange,
  value,
  options,
  errorMessage,
  label,
  tooltip,
  width,
  height,
  components,
  controlProps,
  formatOptionLabel,
  placeholder,
  noIndicatorAnimation,
  anchor,
  isRequired,
  isMulti,
  hideSelectedOptions = true,
  labelProps,
  labelRightElement,
  tooltipProps,
  dataTestId,
  isDisabled = false,
  disabledTooltip,
  indicatorStyling,
  hint,
  ...props
}: IFormSelectProps) => {
  const styles = customStyles({
    isInvalid,
    width,
    height,
    controlProps,
    indicatorStyling,
    noIndicatorAnimation,
    isMulti,
    isPortaled: !!anchor,
  })

  // `options` may be grouped (`{ label, options }`), in which case the selected value lives one
  // level down — resolve against a flattened list so grouped selects still show their value.
  const flatOptions = options?.flatMap((option: any) => (Array.isArray(option?.options) ? option.options : [option])) as
    | Option[]
    | undefined

  const defaultOption = () => {
    if (!isMulti || !flatOptions || !value || !Array.isArray(value)) {
      return undefined
    }
    const arrayValues = [...value] as any[]
    return flatOptions.filter(option => arrayValues.includes(option.value))
  }

  const resolvedFormatOptionLabel = formatOptionLabel ?? renderOptionWithTag

  const selectElement = (
    <ReactSelect
      hideSelectedOptions={hideSelectedOptions}
      name={name}
      closeMenuOnSelect={!isMulti}
      menuPortalTarget={anchor}
      styles={styles}
      formatOptionLabel={resolvedFormatOptionLabel}
      onChange={(val: any, actionMeta) => {
        if (Array.isArray(val)) {
          const value = val.map((v: any) => v.value)
          // @ts-ignore
          onChange?.(value, actionMeta, val)
        } else {
          onChange?.(val.value, actionMeta, val)
        }
      }}
      placeholder={placeholder ?? null}
      // value={options?.find((c: any) => c.value === value) ?? placeholder}
      // @ts-ignore
      value={isMulti ? flatOptions?.filter((c: any) => value.includes(c.value)) : flatOptions?.find((c: any) => c.value === value) ?? null}
      options={options}
      defaultValue={defaultOption()}
      isMulti={isMulti}
      components={{
        ...components,
      }}
      {...props}
      isDisabled={isDisabled}
    />
  )

  return (
    <FormControl isInvalid={true} data-testid={dataTestId} isRequired={isRequired}>
      {(label || tooltip || labelRightElement) && (
        <FlexRow
          justifyContent={labelRightElement ? 'space-between' : 'flex-start'}
          marginBottom={2}
          alignItems={'center'}
          gap='8px'
          width='100%'
          flexWrap='wrap'
        >
          <FlexRow alignItems='center' gap='4px'>
            <FormLabel color={appColors.secondaryForeground} fontSize='14px' marginRight='0px' marginBottom={0} {...labelProps}>
              {label}
            </FormLabel>
            {tooltip && <BaseTooltip width={'16px'} height={'16px'} label={tooltip} blackIcon {...tooltipProps} />}
          </FlexRow>
          {labelRightElement}
        </FlexRow>
      )}
      {isDisabled && disabledTooltip ? (
        <Tooltip label={disabledTooltip} bg='white' color='gray.800' fontWeight='normal' p='4' borderRadius='base' hasArrow>
          <Box pointerEvents='auto' width='100%' display='inline-block'>
            {selectElement}
          </Box>
        </Tooltip>
      ) : (
        selectElement
      )}
      {hint && <Box mt='8px'>{hint}</Box>}
      {isInvalid && errorMessage ? (
        <FormErrorMessage position='absolute' bottom='-6' top='auto' fontSize='12px'>
          {errorMessage}
        </FormErrorMessage>
      ) : null}
    </FormControl>
  )
}
